/**
 * Guardy tras (PLAN §8.1). Guard w przeglądarce jest wygodą nawigacyjną —
 * właściwą kontrolą jest RBAC po stronie serwera (§5.3), który i tak odrzuci
 * żądanie bez uprawnienia.
 */
import { Navigate, Outlet, useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { hasPermission, isRole } from '@k1p4/shared'
import type { Permission } from '@k1p4/shared'
import { Spinner } from '@k1p4/ui'
import { AccessDenied } from '../pages/app/AccessDenied'
import { firstAllowedAppPath } from '../nav'
import { useAuth } from './AuthProvider'
import styles from './guards.module.css'

export function LoadingScreen() {
  const { t } = useTranslation()
  return (
    <div className={styles.loading}>
      <Spinner label={t('common.loading')} />
    </div>
  )
}

/** Wymaga zalogowania — bez sesji ekran logowania (portal 33rp). */
export function RequireAuth() {
  const { session, loading } = useAuth()
  const location = useLocation()

  if (loading) return <LoadingScreen />
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }
  return <Outlet />
}

/**
 * Portal `/me` tylko dla konta z „Portal : Własne dane" (PLAN §8.1: „/me —
 * guard: employee", grupa 113). Konto bez portalu trafia do `/app`, gdzie
 * AppEntry wybiera pierwszy dostępny moduł albo mówi o braku dostępu.
 */
export function RequirePortal() {
  const { session, loading } = useAuth()

  if (loading) return <LoadingScreen />
  if (!session) return <Navigate to="/login" replace />
  if (!hasPermission(session.user.roles.filter(isRole), 'Portal : Własne dane')) {
    return <Navigate to="/app" replace />
  }
  return <Outlet />
}

/**
 * Wymaga któregokolwiek z uprawnień modułu. Zamiast przekierowania pokazujemy
 * EKRAN ODMOWY w tym samym miejscu, w którym stanąłby moduł: użytkownik trafił
 * tu z ręki albo ze starego linku i musi wiedzieć, że rzecz istnieje, ale nie
 * dla jego roli. Ciche przekierowanie wyglądałoby jak zgubiony klik.
 *
 * Guard jest wygodą — właściwą kontrolą pozostaje RBAC serwera (§5.3), który
 * odmawia i zapisuje odmowę w audycie.
 */
export function RequirePermission({ anyOf }: { anyOf: readonly Permission[] }) {
  const { session, loading } = useAuth()

  if (loading) return <LoadingScreen />
  if (!session) return <Navigate to="/login" replace />
  const roles = session.user.roles.filter(isRole)
  if (!anyOf.some((permission) => hasPermission(roles, permission))) {
    return <AccessDenied />
  }
  return <Outlet />
}

/**
 * Wejście `/app`: prowadzimy do pierwszego modułu, który ta rola otworzy.
 * Sztywny skok na pulpit dawał przełożonemu ekran, którego serwer nie policzy.
 */
export function AppEntry() {
  const { session } = useAuth()
  const target = firstAllowedAppPath(session?.user.roles ?? [])
  return target === null ? <AccessDenied /> : <Navigate to={target} replace />
}

/** Miejsce pracy zalogowanego: panel dla ról kadrowych, portal dla pracownika. */
export function homePathFor(roles: readonly string[]): '/app' | '/me' {
  return roles.some((role) => role !== 'employee') ? '/app' : '/me'
}

/**
 * Ekrany tożsamości: zalogowanego odsyłamy tam, gdzie ma pracować — na
 * ścieżkę, z której guard odesłał go na logowanie (`state.from`), a bez niej
 * na start z roli. To ta sama reguła, którą callback portalu stosuje do
 * `?next=`; tutaj działa, gdy sesja pojawia się bez opuszczania SPA (wejście
 * obrazu demonstracyjnego, ADR-0059).
 */
export function RedirectIfAuthenticated() {
  const { session, loading } = useAuth()
  const location = useLocation()

  if (loading) return <LoadingScreen />
  if (session) {
    const from = (location.state as { from?: unknown } | null)?.from
    const target =
      typeof from === 'string' && /^\/(app|me)(\/|$)/.test(from) ? from : homePathFor(session.user.roles)
    return <Navigate to={target} replace />
  }
  return <Outlet />
}
